// ============================================================
// Replay Store — Zustand store that syncs board + narration + progress + eval
// Central state management for the entire replay experience
// ============================================================

import { create } from "zustand"
import type {
  ReplayData,
  MoveData,
  MoveCommentary,
  PhaseNarration,
  MomentCallout,
} from "@shared/types"

// --- Types ---

export type ReplayMode = "guided" | "explore"
export type ReplayStatus = "intro" | "playing" | "ended"

interface ReplayState {
  // Core data
  replayData: ReplayData | null
  mode: ReplayMode
  status: ReplayStatus

  // Position tracking
  currentMoveIndex: number // -1 = starting position, 0 = first move, etc.
  totalMoves: number

  // Auto-play
  isAutoPlaying: boolean
  autoPlayTimerRef: number | null

  // Board orientation
  boardOrientation: "white" | "black"

  // Derived state (computed from currentMoveIndex)
  // These are kept as getters via selectors, not stored

  // Actions
  initReplay: (data: ReplayData) => void
  stepForward: () => void
  stepBackward: () => void
  goToMove: (index: number) => void
  jumpToNextSegment: () => void
  jumpToPreviousSegment: () => void
  startReplay: () => void
  toggleAutoPlay: () => void
  stopAutoPlay: () => void
  flipBoard: () => void
  switchToExplore: () => void
  switchToGuided: () => void
  reset: () => void
}

export const useReplayStore = create<ReplayState>((set, get) => ({
  // --- Initial state ---
  replayData: null,
  mode: "guided",
  status: "intro",
  currentMoveIndex: -1,
  totalMoves: 0,
  isAutoPlaying: false,
  autoPlayTimerRef: null,
  boardOrientation: "white",

  // --- Actions ---

  initReplay: (data) => {
    set({
      replayData: data,
      mode: "guided",
      status: "intro",
      currentMoveIndex: -1,
      totalMoves: data.moves.length,
      isAutoPlaying: false,
      autoPlayTimerRef: null,
      boardOrientation: data.narration.boardOrientation,
    })
  },

  startReplay: () => {
    const { status } = get()
    if (status === "intro") {
      set({ status: "playing", currentMoveIndex: -1 })
    }
  },

  stepForward: () => {
    const { currentMoveIndex, totalMoves, status } = get()
    if (status === "intro" || status === "ended") return
    if (currentMoveIndex >= totalMoves - 1) return
    const nextIndex = currentMoveIndex + 1
    // Transition to "ended" when landing on the last move —
    // the board shows the final position and the panel shows the conclusion
    if (nextIndex >= totalMoves - 1) {
      set({ currentMoveIndex: nextIndex, status: "ended" })
      get().stopAutoPlay()
      return
    }
    set({ currentMoveIndex: nextIndex, status: "playing" })
  },

  stepBackward: () => {
    const { currentMoveIndex, status } = get()
    if (status === "intro") return
    if (currentMoveIndex <= -1) return
    const prevIndex = currentMoveIndex - 1
    set({
      currentMoveIndex: prevIndex,
      status: prevIndex < 0 ? "playing" : "playing",
    })
  },

  goToMove: (index) => {
    const { totalMoves, status } = get()
    if (status === "intro") return
    const clamped = Math.max(-1, Math.min(index, totalMoves - 1))
    const newStatus = clamped >= totalMoves - 1 ? "ended" : "playing"
    set({ currentMoveIndex: clamped, status: newStatus })
    if (newStatus === "ended") {
      get().stopAutoPlay()
    }
  },

  jumpToNextSegment: () => {
    const { replayData, currentMoveIndex, status } = get()
    if (!replayData || status === "intro") return

    const phases = replayData.narration.phases
    const currentMoveNumber = getMoveNumberFromIndex(currentMoveIndex, replayData.moves)

    // Find the next segment start after current position
    for (const phase of phases) {
      if (phase.moveStart > currentMoveNumber) {
        // Jump to the first move of this segment
        const targetIndex = findMoveIndex(replayData.moves, phase.moveStart, "white")
        if (targetIndex !== -1) {
          get().goToMove(targetIndex)
          return
        }
      }
    }

    // If no next segment, go to end
    get().goToMove(replayData.moves.length - 1)
  },

  jumpToPreviousSegment: () => {
    const { replayData, currentMoveIndex, status } = get()
    if (!replayData || status === "intro") return

    const phases = replayData.narration.phases
    const currentMoveNumber = getMoveNumberFromIndex(currentMoveIndex, replayData.moves)

    // Find the current segment, then go to the previous one's start
    for (let i = phases.length - 1; i >= 0; i--) {
      const phase = phases[i]
      if (phase.moveStart < currentMoveNumber) {
        const targetIndex = findMoveIndex(replayData.moves, phase.moveStart, "white")
        if (targetIndex !== -1) {
          get().goToMove(targetIndex)
          return
        }
      }
    }

    // If no previous segment, go to start
    get().goToMove(-1)
  },

  toggleAutoPlay: () => {
    const { isAutoPlaying } = get()
    if (isAutoPlaying) {
      get().stopAutoPlay()
    } else {
      set({ isAutoPlaying: true })
      runAutoPlay(get, set)
    }
  },

  stopAutoPlay: () => {
    const { autoPlayTimerRef } = get()
    if (autoPlayTimerRef !== null) {
      clearTimeout(autoPlayTimerRef)
    }
    set({ isAutoPlaying: false, autoPlayTimerRef: null })
  },

  flipBoard: () => {
    const { boardOrientation } = get()
    set({ boardOrientation: boardOrientation === "white" ? "black" : "white" })
  },

  switchToExplore: () => {
    get().stopAutoPlay()
    set({ mode: "explore" })
  },

  switchToGuided: () => {
    set({ mode: "guided" })
  },

  reset: () => {
    get().stopAutoPlay()
    set({
      mode: "guided",
      status: "intro",
      currentMoveIndex: -1,
      isAutoPlaying: false,
      autoPlayTimerRef: null,
    })
  },
}))

// --- Auto-play engine ---

function runAutoPlay(
  get: () => ReplayState,
  set: (partial: Partial<ReplayState>) => void
) {
  const state = get()
  if (!state.isAutoPlaying || !state.replayData) return
  if (state.status === "ended") {
    set({ isAutoPlaying: false, autoPlayTimerRef: null })
    return
  }

  // Step forward
  state.stepForward()

  // Calculate delay for next move
  const nextState = get()
  if (nextState.status === "ended" || !nextState.isAutoPlaying) {
    set({ isAutoPlaying: false, autoPlayTimerRef: null })
    return
  }

  const delay = getAutoPlayDelay(nextState)
  const timer = window.setTimeout(() => runAutoPlay(get, set), delay)
  set({ autoPlayTimerRef: timer })
}

function getAutoPlayDelay(state: ReplayState): number {
  if (!state.replayData) return 1500

  const currentMove = state.replayData.moves[state.currentMoveIndex]
  if (!currentMove) return 1500

  const moveNumber = currentMove.moveNumber
  const phases = state.replayData.narration.phases
  const callouts = state.replayData.narration.momentCallouts

  const moveColor = currentMove.color

  // Check if this is the critical moment
  const isCriticalCallout = callouts.some(
    (c) => c.moveNumber === moveNumber && c.color === moveColor && c.arrows.length > 1
  )
  if (isCriticalCallout) return 4000

  // Check if this is a key moment (has a callout)
  const isKeyMoment = callouts.some(
    (c) => c.moveNumber === moveNumber && c.color === moveColor
  )
  if (isKeyMoment) return 3000

  // Check if we just entered a new segment
  const isSegmentStart = phases.some(
    (p) => p.moveStart === moveNumber && currentMove.color === "white"
  )
  if (isSegmentStart) return 2000

  // Normal move
  return 1500
}

// --- Stable empty references (avoid infinite re-render loops) ---

const EMPTY_HIGHLIGHTS: string[] = []

// Cache for getLastMove to return stable references
let lastMoveCacheKey = ""
let lastMoveCacheValue: [string, string] | undefined

// --- Selector helpers ---

export function getCurrentFen(state: ReplayState): string {
  if (!state.replayData) return "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  if (state.currentMoveIndex < 0) {
    return "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  }
  return state.replayData.moves[state.currentMoveIndex].fen
}

export function getCurrentEval(state: ReplayState): number {
  if (!state.replayData || state.currentMoveIndex < 0) return 0
  return state.replayData.moves[state.currentMoveIndex].eval
}

export function getCurrentMove(state: ReplayState): MoveData | null {
  if (!state.replayData || state.currentMoveIndex < 0) return null
  return state.replayData.moves[state.currentMoveIndex]
}

export function getLastMove(state: ReplayState): [string, string] | undefined {
  const move = getCurrentMove(state)
  if (!move) return undefined
  const key = `${move.from}-${move.to}`
  if (key !== lastMoveCacheKey) {
    lastMoveCacheKey = key
    lastMoveCacheValue = [move.from, move.to]
  }
  return lastMoveCacheValue
}

export function getCurrentPhase(state: ReplayState): PhaseNarration | null {
  if (!state.replayData || state.currentMoveIndex < 0) return null
  const move = state.replayData.moves[state.currentMoveIndex]
  if (!move) return null

  const phases = state.replayData.narration.phases
  for (let i = phases.length - 1; i >= 0; i--) {
    if (move.moveNumber >= phases[i].moveStart) {
      // Boundary fix: if we're exactly at a phase boundary on White's
      // half-move, stay on the previous phase if it also covers this
      // moveNumber. This prevents "spoiling" Black's upcoming move.
      if (
        move.moveNumber === phases[i].moveStart &&
        move.color === "white" &&
        i > 0
      ) {
        const prevPhase = phases[i - 1]
        if (prevPhase.moveEnd >= move.moveNumber) {
          return prevPhase
        }
      }
      return phases[i]
    }
  }
  return phases[0] ?? null
}

export function getCurrentCallout(state: ReplayState): MomentCallout | null {
  if (!state.replayData || state.currentMoveIndex < 0) return null
  const move = state.replayData.moves[state.currentMoveIndex]
  if (!move) return null

  return (
    state.replayData.narration.momentCallouts.find(
      (c) => c.moveNumber === move.moveNumber && c.color === move.color
    ) ?? null
  )
}

// Cache for getCurrentMoveCommentary
let moveCommentaryCacheKey = ""
let moveCommentaryCacheValue: MoveCommentary | null = null

export function getCurrentMoveCommentary(state: ReplayState): MoveCommentary | null {
  if (!state.replayData || state.currentMoveIndex < 0) return null
  const move = state.replayData.moves[state.currentMoveIndex]
  if (!move) return null

  const key = `${move.moveNumber}-${move.color}`
  if (key === moveCommentaryCacheKey) return moveCommentaryCacheValue

  moveCommentaryCacheKey = key
  moveCommentaryCacheValue = state.replayData.narration.moveCommentary.find(
    (c) => c.moveNumber === move.moveNumber && c.color === move.color
  ) ?? null

  return moveCommentaryCacheValue
}

export function getBoardHighlights(state: ReplayState): string[] {
  const commentary = getCurrentMoveCommentary(state)
  if (!commentary?.keySquares || commentary.keySquares.length === 0) return EMPTY_HIGHLIGHTS
  return commentary.keySquares
}

// --- Material calculation ---

export interface MaterialScore {
  white: number
  black: number
  diff: number // positive = white advantage
}

const DEFAULT_MATERIAL: MaterialScore = { white: 0, black: 0, diff: 0 }
let materialCacheKey = ""
let materialCacheValue: MaterialScore = DEFAULT_MATERIAL

export function getMaterialScore(state: ReplayState): MaterialScore {
  if (!state.replayData) return DEFAULT_MATERIAL

  const cacheKey = `${state.currentMoveIndex}`
  if (cacheKey === materialCacheKey) return materialCacheValue

  // Count material from FEN
  const fen = state.currentMoveIndex < 0
    ? "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR"
    : state.replayData.moves[state.currentMoveIndex].fen.split(" ")[0]

  let white = 0
  let black = 0

  for (const ch of fen) {
    switch (ch) {
      case "P": white += 1; break
      case "N": white += 3; break
      case "B": white += 3; break
      case "R": white += 5; break
      case "Q": white += 9; break
      case "p": black += 1; break
      case "n": black += 3; break
      case "b": black += 3; break
      case "r": black += 5; break
      case "q": black += 9; break
    }
  }

  materialCacheKey = cacheKey
  materialCacheValue = { white, black, diff: white - black }
  return materialCacheValue
}

// Cache for getSegmentProgress
const EMPTY_SEGMENT_PROGRESS = { currentSegmentIndex: 0, totalSegments: 0, segmentBoundaries: [] as number[] }
let segmentProgressCache: { currentSegmentIndex: number; totalSegments: number; segmentBoundaries: number[] } = EMPTY_SEGMENT_PROGRESS
let segmentProgressCacheKey = ""

export function getSegmentProgress(state: ReplayState): {
  currentSegmentIndex: number
  totalSegments: number
  segmentBoundaries: number[] // move indices where segments start
} {
  if (!state.replayData) return EMPTY_SEGMENT_PROGRESS

  const cacheKey = `${state.currentMoveIndex}-${state.replayData.narration.phases.length}`
  if (cacheKey === segmentProgressCacheKey) return segmentProgressCache

  const phases = state.replayData.narration.phases
  const moves = state.replayData.moves
  const boundaries: number[] = []

  for (const phase of phases) {
    const idx = findMoveIndex(moves, phase.moveStart, "white")
    if (idx !== -1) boundaries.push(idx)
  }

  const currentMoveNumber =
    state.currentMoveIndex >= 0
      ? moves[state.currentMoveIndex]?.moveNumber ?? 0
      : 0

  let currentSegmentIndex = 0
  for (let i = phases.length - 1; i >= 0; i--) {
    if (currentMoveNumber >= phases[i].moveStart) {
      currentSegmentIndex = i
      break
    }
  }

  segmentProgressCacheKey = cacheKey
  segmentProgressCache = {
    currentSegmentIndex,
    totalSegments: phases.length,
    segmentBoundaries: boundaries,
  }
  return segmentProgressCache
}

// --- Utility helpers ---

function getMoveNumberFromIndex(index: number, moves: MoveData[]): number {
  if (index < 0 || !moves[index]) return 0
  return moves[index].moveNumber
}

function findMoveIndex(
  moves: MoveData[],
  moveNumber: number,
  color: "white" | "black"
): number {
  return moves.findIndex(
    (m) => m.moveNumber === moveNumber && m.color === color
  )
}
